import styled, { css, keyframes } from 'styled-components';

import { DataView as DataViewComponent } from 'components';
import { resetButton, withDecoratedScroll } from 'styles/common';

// INFO: change if image changed
const IMAGE_WIDTH = 2000;
const IMAGE_HEIGHT = 1116;

const C = {
  primary: '#00e5ff',
  secondary: '#005f73',
  accent: '#ff6b35',
  positive: '#00e676',
  negative: '#ff1744',
  text: 'rgba(180, 220, 240, 0.95)',
  textDim: 'rgba(120, 160, 190, 0.85)',
  title: 'rgba(220, 245, 255, 0.95)',
  glow: 'rgba(0, 229, 255, 0.3)',
  glowStrong: 'rgba(0, 229, 255, 0.6)',
  border: 'rgba(0, 229, 255, 0.15)',
  borderStrong: 'rgba(0, 229, 255, 0.3)',
  bg: 'rgba(0, 20, 40, 0.4)',
  bgSolid: 'rgba(0, 15, 30, 0.7)',
  link: '#00e5ff',
  inputBg: 'rgba(0, 20, 40, 0.5)',
  btnHovered: 'rgba(0, 229, 255, 0.08)',
};

const FONT_MONO = "'Share Tech Mono', monospace";
const FONT_LABEL = "'Rajdhani', sans-serif";

const TRANSITION_COLOR = '#010101';

export const Root = styled.div<{ $visible: boolean }>`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: ${TRANSITION_COLOR};
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 400ms ease;

  @supports (height: 100dvh) {
    height: 100dvh;
  }
`;

export const Overlay = styled.div<{ $hiding: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 50;
  background: ${TRANSITION_COLOR};
  opacity: ${(props) => (props.$hiding ? 0 : 1)};
  transition: opacity 600ms ease;
  pointer-events: none;
`;

export const SpaceWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

export const CockpitWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;
`;

export const CockpitInner = styled.div`
  position: relative;
  min-width: 100vw;
  min-height: 100vh;
  aspect-ratio: ${IMAGE_WIDTH} / ${IMAGE_HEIGHT};
  flex-shrink: 0;

  @supports (height: 100dvh) {
    min-height: 100dvh;
  }
`;

export const CockpitImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
`;

export const Screen = styled.div`
  position: absolute;
  width: 66%;
  height: 52%;
  left: 17%;
  top: 20%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: 900px) {
    height: calc(52% - env(safe-area-inset-bottom, 0px));
  }
`;

// ═══ KEYFRAMES ═══
const hudFadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const hudFlicker = keyframes`
  0%, 100% { opacity: 1; }
  92%      { opacity: 1; }
  93%      { opacity: 0.7; }
  94%      { opacity: 1; }
  97%      { opacity: 0.8; }
  98%      { opacity: 1; }
`;

export const Portfolio = styled.div<{ $hasTab?: boolean }>`
  width: 100%;
  max-width: 100vw;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas:
    'top    top    top'
    'left   center right'
    'bottom bottom bottom';
  gap: 0;
  position: relative;
  z-index: 1;
  animation: ${hudFadeIn} 0.4s ease-out;

  /* scanline overlay */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 100;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0, 229, 255, 0.015) 2px,
      rgba(0, 229, 255, 0.015) 4px
    );
  }
`;

/* ═══════════════════════════════════════════
   TOP BAR
   ═══════════════════════════════════════════ */
export const TopBar = styled.div`
  grid-area: top;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  border-bottom: 1px solid ${C.border};
  font-family: ${FONT_LABEL};
  font-size: 13px;
  color: ${C.textDim};
  animation: ${hudFlicker} 8s infinite;

  @media (max-width: 600px) {
    gap: 8px;
    padding: 8px 10px;
  }
`;

export const TopBarTitle = styled.span`
  font-family: ${FONT_LABEL};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${C.primary};
  text-shadow: 0 0 8px ${C.glow};

  @media (max-width: 600px) {
    font-size: 14px;
    letter-spacing: 1px;
  }
`;

export const StatusBadge = styled.span<{ $isOnline: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid ${(props) => (props.$isOnline ? C.positive : C.negative)};
  color: ${(props) => (props.$isOnline ? C.positive : C.negative)};
  text-shadow: 0 0 4px ${(props) => (props.$isOnline ? C.positive : C.negative)};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${(props) => (props.$isOnline ? C.positive : C.negative)};
    box-shadow: 0 0 4px ${(props) => (props.$isOnline ? C.positive : C.negative)};
  }
`;

export const TopBarSpacer = styled.span`
  flex: 1;

  @media (max-width: 600px) {
    display: none;
  }
`;

export const TopBarInfo = styled.span`
  font-family: ${FONT_MONO};
  font-size: 12px;
  color: ${C.textDim};

  @media (max-width: 600px) {
    font-size: 11px;
    max-width: 100%;
  }
`;

export const TopBarAccount = styled(TopBarInfo)`
  @media (max-width: 600px) {
    margin-left: auto;
    min-width: 0;
  }
`;

export const TopBarNetwork = styled(TopBarInfo)`
  @media (max-width: 600px) {
    display: none;
  }
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    gap: 4px;
    flex-shrink: 0;
  }
`;

export const SyncButtonLabel = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

export const SyncButtonIcon = styled.span`
  display: none;
  font-size: 15px;
  line-height: 1;

  @media (max-width: 600px) {
    display: inline;
  }
`;

export const SyncButton = styled.button`
  ${resetButton}
  font-family: ${FONT_MONO};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.primary};
  cursor: pointer;
  padding: 2px 8px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  transition: all 0.2s;

  &:hover {
    border-color: ${C.primary};
    text-shadow: 0 0 6px ${C.glow};
    box-shadow: 0 0 8px ${C.glow};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    margin-left: 0;
    min-width: 28px;
    justify-content: center;
    padding: 2px 6px;
    font-size: 11px;
  }
`;

/* ═══════════════════════════════════════════
   LEFT PANEL — metrics
   ═══════════════════════════════════════════ */
export const LeftPanel = styled.div`
  grid-area: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 28px;
  padding: 20px 24px;
  animation: ${hudFadeIn} 0.5s ease-out 0.1s both;
`;

export const MetricBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const MetricLabel = styled.div`
  font-family: ${FONT_LABEL};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${C.textDim};
`;

export const MetricValue = styled.div`
  font-family: ${FONT_MONO};
  font-size: 22px;
  color: ${C.title};
  text-shadow: 0 0 10px ${C.glow};
  line-height: 1.3;
`;

export const MetricSub = styled.div`
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.textDim};
`;

/* ═══════════════════════════════════════════
   RIGHT PANEL — counters + buttons
   ═══════════════════════════════════════════ */
export const RightPanel = styled.div`
  grid-area: right;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 20px;
  padding: 20px 24px;
  animation: ${hudFadeIn} 0.5s ease-out 0.15s both;
`;

export const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const StatValue = styled.div`
  font-family: ${FONT_MONO};
  font-size: 28px;
  color: ${C.primary};
  text-shadow: 0 0 12px ${C.glow};
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-family: ${FONT_LABEL};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${C.textDim};
`;

export const RightButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

/* ═══════════════════════════════════════════
   CENTER PANEL — tab content
   ═══════════════════════════════════════════ */
export const CenterPanel = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 16px;
  animation: ${hudFadeIn} 0.3s ease-out;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.border};
    border-radius: 2px;
  }
`;

/* ═══════════════════════════════════════════
   BOTTOM BAR — tabs
   ═══════════════════════════════════════════ */
export const BottomBar = styled.div`
  grid-area: bottom;
  display: flex;
  justify-content: center;
  padding: 6px 16px calc(8px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${C.border};
`;

export const TabRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px;
`;

export const HudTab = styled.button<{ $isActive: boolean }>`
  ${resetButton}
  padding: 6px 16px;
  font-family: ${FONT_LABEL};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 2px;
  transition: all 0.15s;

  ${(props) =>
    props.$isActive
      ? css`
          color: ${C.primary};
          border-color: ${C.primary};
          text-shadow: 0 0 6px ${C.glow};
          box-shadow: 0 0 8px ${C.glow};
          background: rgba(0, 229, 255, 0.06);
        `
      : css`
          color: ${C.textDim};
          background: transparent;

          &:hover {
            color: ${C.text};
            border-color: ${C.border};
            background: ${C.btnHovered};
          }
        `}

  @media (max-width: 600px) {
    padding: 6px 8px;
  }
`;

/* ═══════════════════════════════════════════
   HUD BUTTON
   ═══════════════════════════════════════════ */
export const HudButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  ${resetButton}
  padding: 6px 16px;
  font-family: ${FONT_MONO};
  font-size: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid ${C.border};
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${C.text};
  background: transparent;

  ${(props) =>
    props.$variant === 'primary' &&
    css`
      color: ${C.primary};
      border-color: ${C.primary};

      &:hover {
        background: rgba(0, 229, 255, 0.1);
        box-shadow: 0 0 10px ${C.glow};
      }
    `}

  ${(props) =>
    props.$variant === 'danger' &&
    css`
      color: ${C.negative};
      border-color: rgba(255, 23, 68, 0.5);

      &:hover {
        background: rgba(255, 23, 68, 0.1);
        box-shadow: 0 0 10px rgba(255, 23, 68, 0.3);
      }
    `}

  ${(props) =>
    !props.$variant &&
    css`
      &:hover {
        border-color: ${C.borderStrong};
        color: ${C.title};
        background: ${C.btnHovered};
      }
    `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ═══════════════════════════════════════════
   CONNECT WALLET
   ═══════════════════════════════════════════ */
export const ConnectWalletWrapper = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  button {
    max-width: 320px;
    font-family: ${FONT_MONO};
    letter-spacing: 1px;
    text-transform: uppercase;
    border-radius: 2px;
  }
`;

/* ═══════════════════════════════════════════
   ERROR STATE
   ═══════════════════════════════════════════ */
export const ErrorState = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.negative};
  text-shadow: 0 0 6px rgba(255, 23, 68, 0.3);
`;

/* ═══════════════════════════════════════════
   SECTION STYLES (Activity, CopiedWallets)
   ═══════════════════════════════════════════ */
export const ActivitySection = styled.div`
  position: relative;
  padding: 12px;
  border-radius: 2px;
  background: ${C.bg};
  border: 1px solid ${C.border};
  backdrop-filter: blur(4px);
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SectionDot = styled.div<{ color?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.color || C.accent};
  box-shadow: 0 0 6px ${(props) => props.color || C.accent};
  flex-shrink: 0;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${FONT_LABEL};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.title};
`;

/* ═══════════════════════════════════════════
   TABLE
   ═══════════════════════════════════════════ */
export const TableWrapper = styled.div`
  ${withDecoratedScroll}
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.border};
    border-radius: 2px;
  }
`;

export const Table = styled.table<{ isScrolled?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  tbody tr {
    background: transparent;
    border-bottom: 1px solid ${C.border};
    transition: background 0.1s;
  }

  tbody tr:hover {
    background: rgba(0, 229, 255, 0.04);
  }

  th,
  td {
    padding: 8px 10px;
    font-family: ${FONT_MONO};
    font-size: 13px;
  }

  th {
    font-family: ${FONT_LABEL};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${C.textDim};
    white-space: nowrap;
    text-align: left;
    border-bottom: 1px solid ${C.borderStrong};
  }

  td {
    color: ${C.text};
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    padding-left: 12px;
  }

  th:last-child,
  td:last-child {
    padding-right: 12px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${C.bgSolid};
    backdrop-filter: blur(6px);
  }

  ${(props) =>
    props.isScrolled &&
    css`
      thead {
        box-shadow: 0 1px 8px rgba(0, 229, 255, 0.1);
      }
    `}
`;

/* ═══════════════════════════════════════════
   STYLED DATAVIEW (mono font override)
   ═══════════════════════════════════════════ */
export const DataView = styled(DataViewComponent)`
  font-family: ${FONT_MONO};
`;

export const AddressLink = styled.a`
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.link};
  text-decoration: none;

  &:hover {
    text-shadow: 0 0 6px ${C.glow};
  }
`;

export const CloseTabButton = styled.button`
  ${resetButton}
  padding: 6px 10px;
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.negative};
  border: 1px solid rgba(255, 23, 68, 0.3);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s;
  margin-left: 8px;

  &:hover {
    border-color: ${C.negative};
    background: rgba(255, 23, 68, 0.08);
    text-shadow: 0 0 4px rgba(255, 23, 68, 0.4);
  }
`;
